import * as THREE from "three";
import { Rng } from "@dyson/shared";

/**
 * The inside of the Dyson sphere, seen side-on. The play area is one slice
 * through the sphere, so the shell shows up as a riveted circular wall around
 * the edge, with the far side of the sphere as a distant backdrop for
 * parallax. No stars: the shell is sealed.
 */
export function buildShell(radius: number): THREE.Group {
  const group = new THREE.Group();
  group.name = "shell";
  const charge = chargeUniforms();
  group.userData.charge = charge;
  const seams = makeSeamTexture();

  const wallTex = makePanelTexture();
  wallTex.wrapS = wallTex.wrapT = THREE.RepeatWrapping;
  wallTex.repeat.set(160, 2);
  const wall = new THREE.Mesh(
    new THREE.RingGeometry(radius, radius + 400, 256, 1),
    chargedPanels(new THREE.MeshBasicMaterial({ map: wallTex, color: 0x8a8a96 }), seams, charge),
  );
  remapRingUvs(wall.geometry as THREE.RingGeometry, 256, radius, 400);
  group.add(wall);

  const backTex = makePanelTexture();
  backTex.wrapS = backTex.wrapT = THREE.RepeatWrapping;
  backTex.repeat.set(40, 40);
  const back = new THREE.Mesh(
    new THREE.PlaneGeometry(radius * 4, radius * 4),
    chargedPanels(new THREE.MeshBasicMaterial({ map: backTex, color: 0x6a6670 }), seams, charge),
  );
  back.position.z = -600;
  group.add(back);

  group.add(buildBackdropRocks(radius));
  return group;
}

/**
 * By day the shell is live: a faint charge runs along the seams between its
 * panels (it is what keeps the storms and the night things off). Each run of
 * seam carries its own share, some none, and it holds steady. When a sun goes
 * out the charge goes with it: dimming slowly in patches while it falters,
 * dying from the edge of its reach in as it dims, coming back out from it as
 * it relights (the same way the dark walks; see outageLight).
 */
export interface ShellCharge {
  /** 0 (dead) .. 1 (fully live). */
  charge: number;
  /** 0..1, how much the seams waver (a faltering sun). */
  flicker: number;
  /** The sun whose outage it is, if any: its centre and radius, and how far its light reaches past its surface. */
  sun: { x: number; y: number; radius: number; reach: number } | null;
  /** The renderer's clock (s). */
  time: number;
}

export function setShellCharge(shell: THREE.Object3D, c: ShellCharge): void {
  const u = shell.userData.charge as ReturnType<typeof chargeUniforms> | undefined;
  if (!u) return;
  u.uCharge.value = c.charge;
  u.uFlicker.value = c.flicker;
  u.uTime.value = c.time;
  if (c.sun) u.uSun.value.set(c.sun.x, c.sun.y, c.sun.radius);
  else u.uSun.value.set(0, 0, -1);
  u.uReach.value = c.sun?.reach ?? 1;
}

/** Shared by the wall and the backdrop, so one update drives both. */
function chargeUniforms() {
  return {
    uSeams: { value: null as THREE.Texture | null },
    uSeamColor: { value: new THREE.Color("rgb(10,22,28)") },
    uCharge: { value: 1 },
    uFlicker: { value: 0 },
    uTime: { value: 0 },
    uSun: { value: new THREE.Vector3(0, 0, -1) },
    uReach: { value: 1 },
  };
}

/**
 * The panels' own material, with the seams' charge added in the same pass.
 * The charge is painted once into a texture (see makeSeamTexture), so the
 * GPU's mipmaps keep it steady at any zoom; the shader only decides how much
 * of it is live. Added after fog, so the far wall's haze doesn't swallow it.
 */
function chargedPanels(material: THREE.MeshBasicMaterial, seams: THREE.Texture, charge: ReturnType<typeof chargeUniforms>): THREE.MeshBasicMaterial {
  charge.uSeams.value = seams;
  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, charge);
    shader.vertexShader = shader.vertexShader
      .replace("#include <common>", "#include <common>\nvarying vec2 vShellWorld;")
      .replace("#include <project_vertex>", "#include <project_vertex>\nvShellWorld = (modelMatrix * vec4(transformed, 1.0)).xy;");
    shader.fragmentShader = shader.fragmentShader
      .replace("#include <common>", `#include <common>\n${SEAM_UNIFORMS}`)
      .replace("#include <fog_fragment>", `#include <fog_fragment>\n${SEAM_FRAGMENT}`);
  };
  return material;
}

/** Panels across one tile of the panel texture (see makePanelTexture); the seam texture has twice as many, so it repeats half as often. */
const PANELS = 8;

const SEAM_UNIFORMS = /* glsl */ `
  uniform sampler2D uSeams;
  uniform vec3 uSeamColor;
  uniform float uCharge;
  uniform float uFlicker;
  uniform float uTime;
  uniform vec3 uSun;
  uniform float uReach;
  varying vec2 vShellWorld;
`;

// r: how bright the seam is here; g: when its segment goes (0 first .. 1 last); b: its phase when wavering.
const SEAM_FRAGMENT = /* glsl */ `
  {
    vec4 seam = texture2D(uSeams, vMapUv * 0.5);
    // It goes when the charge drops below its threshold: further from the sun, sooner.
    float reach = uSun.z < 0.0 ? 0.5 : clamp((distance(vShellWorld, uSun.xy) - uSun.z) / uReach, 0.0, 1.0);
    float th = 0.12 + 0.6 * reach + 0.25 * seam.g;
    float on = smoothstep(th - 0.04, th + 0.04, uCharge * 1.05);
    // A faltering sun: each segment slowly dims and comes back up, never quite out.
    on *= 1.0 - uFlicker * 0.7 * (0.5 - 0.5 * sin(uTime * 0.4 + seam.b * 6.2832));
    gl_FragColor.rgb += uSeamColor * seam.r * on;
  }
`;

/**
 * The seams' charge, as data (see SEAM_FRAGMENT): soft lines along the panel
 * edges in red, each panel edge with its own brightness (some dead), and its
 * own threshold and phase in green and blue, filled out over its panel so
 * the far mipmaps still average to something sensible.
 */
function makeSeamTexture(): THREE.CanvasTexture {
  const cells = PANELS * 2;
  const cell = 64;
  const size = cells * cell;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  const rng = new Rng(0x5ea3);
  const byte = (): number => Math.floor(rng.next() * 256);

  for (let y = 0; y < cells; y++) {
    for (let x = 0; x < cells; x++) {
      ctx.fillStyle = `rgb(0,${byte()},${byte()})`;
      ctx.fillRect(x * cell, y * cell, cell, cell);
    }
  }

  // Each panel edge, in pixels, with its own share of the charge. An edge on
  // the tile's left or top is drawn again on its right or bottom, so it tiles.
  const edges: { x0: number; y0: number; x1: number; y1: number; g: number; b: number; level: number }[] = [];
  const edge = (x0: number, y0: number, x1: number, y1: number): void => {
    const share = { g: byte(), b: byte(), level: rng.next() < 0.08 ? 0 : 0.35 + 0.65 * rng.next() };
    edges.push({ x0: x0 * cell, y0: y0 * cell, x1: x1 * cell, y1: y1 * cell, ...share });
    if (x0 === 0 && x1 === 0) edges.push({ x0: size, y0: y0 * cell, x1: size, y1: y1 * cell, ...share });
    if (y0 === 0 && y1 === 0) edges.push({ x0: x0 * cell, y0: size, x1: x1 * cell, y1: size, ...share });
  };
  for (let i = 0; i < cells; i++) {
    for (let j = 0; j < cells; j++) {
      edge(i, j, i, j + 1);
      edge(j, i, j + 1, i);
    }
  }
  const halo = 5;
  for (const e of edges) {
    ctx.fillStyle = `rgb(0,${e.g},${e.b})`;
    ctx.fillRect(Math.min(e.x0, e.x1) - halo, Math.min(e.y0, e.y1) - halo, Math.abs(e.x1 - e.x0) + halo * 2, Math.abs(e.y1 - e.y0) + halo * 2);
  }
  // Halo, glow and core, added up (the lighter blend leaves green and blue be) to a soft line.
  ctx.globalCompositeOperation = "lighter";
  ctx.lineCap = "butt";
  for (const e of edges) {
    for (const [r, width] of [[20, 9], [55, 4], [180, 1.5]] as const) {
      ctx.strokeStyle = `rgb(${Math.round(r * e.level)},0,0)`;
      ctx.lineWidth = width;
      ctx.beginPath();
      ctx.moveTo(e.x0, e.y0);
      ctx.lineTo(e.x1, e.y1);
      ctx.stroke();
    }
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.anisotropy = 8;
  return tex;
}

/** Wrap the panel texture around the ring (U = angle, V = depth into the wall). */
function remapRingUvs(geo: THREE.RingGeometry, segments: number, inner: number, thickness: number): void {
  const pos = geo.getAttribute("position") as THREE.BufferAttribute;
  const uv = geo.getAttribute("uv") as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) {
    // RingGeometry lays vertices out ring by ring, `segments + 1` per ring, so
    // the index gives the angle without an atan2 seam.
    const a = (i % (segments + 1)) / segments;
    uv.setXY(i, a, (Math.hypot(pos.getX(i), pos.getY(i)) - inner) / thickness);
  }
  uv.needsUpdate = true;
}

const rockGeometry = new THREE.DodecahedronGeometry(1, 0);
const rockMaterial = new THREE.MeshLambertMaterial({ color: 0x5a5148, flatShading: true });

/** Far-off rocks drifting between the play plane and the back of the sphere. Purely decorative. */
function buildBackdropRocks(radius: number): THREE.InstancedMesh {
  const count = 900;
  const mesh = new THREE.InstancedMesh(rockGeometry, rockMaterial, count);
  mesh.name = "backdrop-rocks";
  const rng = new Rng(0xbac4d20);
  const pos = new THREE.Vector3();
  const quat = new THREE.Quaternion();
  const euler = new THREE.Euler();
  const scale = new THREE.Vector3();
  const matrix = new THREE.Matrix4();
  for (let i = 0; i < count; i++) {
    const a = rng.range(0, Math.PI * 2);
    const r = Math.sqrt(rng.next()) * radius * 1.1;
    pos.set(Math.cos(a) * r, Math.sin(a) * r, rng.range(-900, -120));
    euler.set(rng.range(0, 6.3), rng.range(0, 6.3), rng.range(0, 6.3));
    quat.setFromEuler(euler);
    const s = rng.range(4, 36);
    scale.set(s * rng.range(0.6, 1.4), s * rng.range(0.6, 1.4), s * rng.range(0.6, 1.4));
    matrix.compose(pos, quat, scale);
    mesh.setMatrixAt(i, matrix);
  }
  mesh.instanceMatrix.needsUpdate = true;
  return mesh;
}

function makePanelTexture(): THREE.CanvasTexture {
  const size = 512;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext("2d")!;

  ctx.fillStyle = "#1b1b22";
  ctx.fillRect(0, 0, size, size);

  // Panels with slight tonal variation.
  const cells = PANELS;
  const cell = size / cells;
  let n = 1;
  for (let y = 0; y < cells; y++) {
    for (let x = 0; x < cells; x++) {
      n = (n * 16807) % 2147483647;
      const shade = 24 + (n % 14);
      ctx.fillStyle = `rgb(${shade},${shade},${shade + 6})`;
      ctx.fillRect(x * cell + 2, y * cell + 2, cell - 4, cell - 4);
    }
  }

  // Seams.
  ctx.strokeStyle = "#0c0c10";
  ctx.lineWidth = 3;
  for (let i = 0; i <= cells; i++) {
    ctx.beginPath();
    ctx.moveTo(i * cell, 0);
    ctx.lineTo(i * cell, size);
    ctx.moveTo(0, i * cell);
    ctx.lineTo(size, i * cell);
    ctx.stroke();
  }

  // Rivets.
  ctx.fillStyle = "#3a3a44";
  for (let y = 0; y <= cells; y++) {
    for (let x = 0; x <= cells; x++) {
      ctx.beginPath();
      ctx.arc(x * cell, y * cell, 2.5, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return tex;
}
